import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Wallet, Calendar, History, LogOut, TrendingUp, FileText, Settings as SettingsIcon, BarChart3, Library, Crown, Sparkles, Tag, Users, ChevronDown, ChevronRight, Landmark, Building2, Globe, LineChart, CircleDollarSign, MoreHorizontal, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBilling } from "../context/BillingContext";

const NavItem = ({ to, icon: Icon, children, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-colors ${
        isActive
          ? "border-paars-dark bg-paars/20 text-graphite"
          : "border-transparent text-graphite/60 hover:text-graphite hover:bg-stone/50"
      }`
    }
  >
    <Icon className="w-4 h-4" strokeWidth={1.5} />
    <span className="font-medium">{children}</span>
  </NavLink>
);

// Asset-type child links — clicking takes you to /holdings?type=<key>.
// Order is intentional: most-loved category first, "Other" last.
const POSITION_TYPES = [
  { key: "babybond",  label: "Babybonds", icon: Landmark },
  { key: "preferred", label: "Preferred", icon: Crown },
  { key: "bdc",       label: "BDCs",      icon: Building2 },
  { key: "reit",      label: "REITs",     icon: Globe },
  { key: "etf",       label: "ETFs",      icon: LineChart },
  { key: "common",    label: "Common",    icon: CircleDollarSign },
  { key: "other",     label: "Other",     icon: MoreHorizontal },
];

// Database (universe of tradeable securities) child links.
// `path` differs from POSITION_TYPES because /catalog (legacy babybonds list)
// stays as a separate route, while everything else lives under /database/*.
const DATABASE_TYPES = [
  { key: "babybond",        label: "Babybonds",       icon: Landmark,           path: "/catalog" },
  { key: "bdc",             label: "BDCs",            icon: Building2,          path: "/database/bdc" },
  { key: "bdc_discover",    label: "Discover BDCs",   icon: Sparkles,           path: "/database/bdc/discover" },
  { key: "reit",            label: "REITs",           icon: Globe,              path: "/database/reit" },
  { key: "preferred",       label: "Preferred Stock", icon: Crown,              path: "/database/preferred" },
  { key: "etf",             label: "ETFs",            icon: LineChart,          path: "/database/etf" },
  { key: "dividend_growth", label: "Dividend Growth", icon: TrendingUp,         path: "/database/dividend_growth" },
  { key: "other",           label: "Other",           icon: MoreHorizontal,     path: "/database/other" },
];

const SubNavItem = ({ to, label, icon: Icon, active, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={`flex items-center gap-3 pl-11 pr-4 py-2 text-xs border-l-2 transition-colors ${
      active
        ? "border-paars-dark bg-paars/15 text-graphite font-semibold"
        : "border-transparent text-graphite/55 hover:text-graphite hover:bg-stone/40"
    }`}
  >
    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
    <span>{label}</span>
  </NavLink>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { tier, openUpgrade } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar "Positions" expander — open by default if user is on /holdings.
  // State persisted to localStorage so navigating away/back keeps the user's
  // chosen layout. Test-IDs on every interactive element.
  const [positionsOpen, setPositionsOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("bb_positions_expanded");
      if (saved === "0") return false;
      if (saved === "1") return true;
    } catch (_) { void 0; }
    return location.pathname.startsWith("/holdings") || location.pathname === "/call-risk";
  });

  const togglePositions = () => {
    setPositionsOpen((v) => {
      const next = !v;
      try { localStorage.setItem("bb_positions_expanded", next ? "1" : "0"); } catch (_) { void 0; }
      return next;
    });
  };

  // Determine which asset_type filter (if any) is currently active.
  const activeType = (() => {
    if (!location.pathname.startsWith("/holdings")) return null;
    const params = new URLSearchParams(location.search);
    return params.get("type"); // null → "All" view
  })();
  const positionsActive = location.pathname.startsWith("/holdings") || location.pathname === "/call-risk";
  const callRiskActive = location.pathname === "/call-risk";
  // Database expander — open if on /catalog (babybonds, the legacy route) or any /database/*
  const databaseActive = location.pathname.startsWith("/database") || location.pathname === "/catalog";
  const [databaseOpen, setDatabaseOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("bb_database_expanded");
      if (saved === "0") return false;
      if (saved === "1") return true;
    } catch (_) { void 0; }
    return databaseActive;
  });
  const toggleDatabase = () => {
    setDatabaseOpen((v) => {
      const next = !v;
      try { localStorage.setItem("bb_database_expanded", next ? "1" : "0"); } catch (_) { void 0; }
      return next;
    });
  };

  const tierBadge =
    tier === "founder_lifetime"
      ? { label: "Founder", icon: Crown, cls: "bg-gold text-graphite" }
      : tier === "pro"
      ? { label: "Pro", icon: Sparkles, cls: "bg-paars text-graphite" }
      : { label: "Free", icon: null, cls: "bg-stone text-graphite/70" };
  const BadgeIcon = tierBadge.icon;

  return (
    <div className="min-h-screen bg-champagne text-graphite flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-stone bg-white flex flex-col fixed inset-y-0 left-0">
        <div className="px-5 py-6 border-b border-stone">
          <div className="flex items-center gap-3" onClick={() => navigate("/dashboard")} role="button">
            <div className="w-9 h-9 bg-paars flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-graphite" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-graphite/55">BABYBONDS</div>
              <div className="text-sm font-mono text-graphite">TRACKER</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 space-y-1">
          <div className="px-4 mb-2 text-[10px] tracking-[0.3em] uppercase text-graphite/45 font-bold">// MAIN</div>
          <NavItem to="/dashboard" icon={LayoutDashboard} testid="nav-dashboard">Dashboard</NavItem>
          {/* Database — expandable parent (renamed from Catalog).
              Houses the universe of tradeable securities per asset_type. */}
          <div>
            <button
              data-testid="nav-database-toggle"
              onClick={toggleDatabase}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-colors ${
                databaseActive
                  ? "border-paars-dark bg-paars/20 text-graphite"
                  : "border-transparent text-graphite/60 hover:text-graphite hover:bg-stone/50"
              }`}
            >
              <Library className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-medium flex-1 text-left">Database</span>
              {databaseOpen
                ? <ChevronDown className="w-3.5 h-3.5 text-graphite/55" strokeWidth={2} />
                : <ChevronRight className="w-3.5 h-3.5 text-graphite/55" strokeWidth={2} />}
            </button>
            {databaseOpen && (
              <div data-testid="nav-database-children" className="mt-1 mb-1">
                {DATABASE_TYPES.map((t) => (
                  <SubNavItem
                    key={t.key}
                    to={t.path}
                    label={t.label}
                    icon={t.icon}
                    active={location.pathname === t.path}
                    testid={`nav-database-${t.key}`}
                  />
                ))}
              </div>
            )}
          </div>
          {/* Positions — expandable parent w/ asset-type child links */}
          <div>
            <button
              data-testid="nav-holdings-toggle"
              onClick={togglePositions}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-colors ${
                positionsActive
                  ? "border-paars-dark bg-paars/20 text-graphite"
                  : "border-transparent text-graphite/60 hover:text-graphite hover:bg-stone/50"
              }`}
            >
              <Wallet className="w-4 h-4" strokeWidth={1.5} />
              <span className="font-medium flex-1 text-left">Positions</span>
              {positionsOpen
                ? <ChevronDown className="w-3.5 h-3.5 text-graphite/55" strokeWidth={2} />
                : <ChevronRight className="w-3.5 h-3.5 text-graphite/55" strokeWidth={2} />}
            </button>
            {positionsOpen && (
              <div data-testid="nav-holdings-children" className="mt-1 mb-1">
                <SubNavItem
                  to="/holdings"
                  label="All positions"
                  icon={Wallet}
                  active={positionsActive && !activeType && !callRiskActive}
                  testid="nav-holdings-all"
                />
                {POSITION_TYPES.map((t) => (
                  <SubNavItem
                    key={t.key}
                    to={`/holdings?type=${t.key}`}
                    label={t.label}
                    icon={t.icon}
                    active={!callRiskActive && activeType === t.key}
                    testid={`nav-holdings-${t.key}`}
                  />
                ))}
                <SubNavItem
                  to="/call-risk"
                  label="Call Risk"
                  icon={AlertTriangle}
                  active={callRiskActive}
                  testid="nav-call-risk"
                />
              </div>
            )}
          </div>
          <NavItem to="/dividends" icon={History} testid="nav-dividends">Dividends</NavItem>
          <NavItem to="/calendar" icon={Calendar} testid="nav-calendar">Calendar</NavItem>
          <NavItem to="/ladder" icon={BarChart3} testid="nav-ladder">Yield Ladder</NavItem>
          <NavItem to="/tax-report" icon={FileText} testid="nav-tax">Tax Report</NavItem>
          <NavItem to="/settings" icon={SettingsIcon} testid="nav-settings">Settings</NavItem>
          {user?.is_admin && (
            <>
              <div className="px-4 mt-5 mb-2 text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// ADMIN</div>
              <NavItem to="/admin/users" icon={Users} testid="nav-admin-users">Users</NavItem>
              <NavItem to="/admin/promos" icon={Tag} testid="nav-admin-promos">Promo Codes</NavItem>
            </>
          )}
        </nav>

        <div className="border-t border-stone p-4">
          <a
            href="https://www.dividend.community"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="powered-by-dc-sidebar"
            className="block mb-4 group"
            title="Powered by Dividend Community"
          >
            <div className="text-[9px] font-mono tracking-[0.35em] uppercase text-graphite/45 mb-1.5">// POWERED BY</div>
            <img
              src="/brand/dc-logo-dark.png"
              alt="Dividend Community"
              className="h-7 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
            />
          </a>
          <div data-testid="tier-badge-row" className="flex items-center justify-between mb-3">
            <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-bold ${tierBadge.cls}`}>
              {BadgeIcon && <BadgeIcon className="w-3 h-3" strokeWidth={2.5} />}
              {tierBadge.label}
            </span>
            {tier === "free" && (
              <button
                data-testid="sidebar-upgrade-btn"
                onClick={() => openUpgrade()}
                className="text-[10px] tracking-[0.2em] uppercase text-paars-dark font-bold hover:text-graphite"
              >
                UPGRADE →
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 object-cover" />
            ) : (
              <div className="w-8 h-8 bg-stone flex items-center justify-center text-xs font-mono">
                {user?.name?.[0] || "?"}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs text-graphite truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] font-mono text-graphite/55 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 text-xs text-graphite/60 hover:text-paars-dark transition-colors px-2 py-2"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="tracking-widest uppercase">Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        {children}
      </main>
    </div>
  );
}
